import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * 格式化货币
 */
export function formatCurrency(amount: number, currency: string = 'USD', locale: string = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * 格式化数字
 */
export function formatNumber(
  value: number,
  options: {
    decimals?: number;
    locale?: string;
    style?: 'decimal' | 'percent';
  } = {}
): string {
  const { decimals = 2, locale = 'en-US', style = 'decimal' } = options;

  return new Intl.NumberFormat(locale, {
    style,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(style === 'percent' ? value / 100 : value);
}

/**
 * 格式化重量
 */
export function formatWeight(value: number, unit: 'kg' | 'g' | 'lb' | 'oz' = 'kg'): string {
  const units = {
    kg: { name: 'kg', factor: 1 },
    g: { name: 'g', factor: 1000 },
    lb: { name: 'lb', factor: 2.20462 },
    oz: { name: 'oz', factor: 35.274 }
  };

  const unitInfo = units[unit];
  const displayValue = value * unitInfo.factor;

  return `${displayValue.toFixed(displayValue < 1 ? 3 : 2)} ${unitInfo.name}`;
}

/**
 * 格式化尺寸
 */
export function formatDimensions(
  length: number,
  width: number,
  height: number,
  unit: 'cm' | 'in' = 'cm'
): string {
  const units = {
    cm: { name: 'cm', factor: 1 },
    in: { name: 'in', factor: 0.393701 }
  };

  const unitInfo = units[unit];
  const factor = unitInfo.factor;

  const displayLength = length * factor;
  const displayWidth = width * factor;
  const displayHeight = height * factor;

  const formatValue = (val: number) =>
    val.toFixed(val < 1 ? 3 : 2);

  return `${formatValue(displayLength)} × ${formatValue(displayWidth)} × ${formatValue(displayHeight)} ${unitInfo.name}`;
}

/**
 * 生成唯一ID
 */
export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

/**
 * 防抖函数
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * 节流函数
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * 深拷贝
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T;
  if (typeof obj === 'object') {
    const clonedObj = {} as { [key: string]: any };
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj as T;
  }
  return obj;
}

/**
 * 下载CSV文件
 */
export function downloadCSV(data: any[], filename: string): void {
  const headers = Object.keys(data[0] || {});
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // 处理包含逗号或引号的值
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * 保存到本地存储
 */
export function saveToLocalStorage<T>(key: string, data: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}

/**
 * 从本地存储读取
 */
export function loadFromLocalStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Failed to load from localStorage:', error);
    return defaultValue;
  }
}

/**
 * 获取利润率颜色
 */
export function getProfitMarginColor(margin: number): string {
  if (margin < 0) return 'text-red-600';
  if (margin < 10) return 'text-yellow-600';
  if (margin < 20) return 'text-blue-600';
  return 'text-green-600';
}

/**
 * 获取利润率背景色
 */
export function getProfitMarginBgColor(margin: number): string {
  if (margin < 0) return 'bg-red-50 border-red-200';
  if (margin < 10) return 'bg-yellow-50 border-yellow-200';
  if (margin < 20) return 'bg-blue-50 border-blue-200';
  return 'bg-green-50 border-green-200';
}

/**
 * 计算百分比变化
 */
export function calculatePercentageChange(oldValue: number, newValue: number): number {
  if (oldValue === 0) return newValue > 0 ? 100 : 0;
  return ((newValue - oldValue) / Math.abs(oldValue)) * 100;
}

/**
 * 格式化百分比变化
 */
export function formatPercentageChange(change: number): string {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
}

/**
 * 验证数字输入
 */
export function validateNumericInput(value: string, min?: number, max?: number): boolean {
  const num = parseFloat(value);
  if (isNaN(num)) return false;
  if (min !== undefined && num < min) return false;
  if (max !== undefined && num > max) return false;
  return true;
}

/**
 * 截断文本
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}